import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ExternalLink, TrendingUp, TrendingDown, Palette, RotateCcw, Save, Loader2 } from "lucide-react";

export default function SourcesPanel({ sources, stats, isAdmin = false, onColorsChanged = () => {} }) {
  const most = stats?.most_source;
  const least = stats?.least_source;

  // color editor state (admin only)
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/admin/source-colors")
      .then((r) => {
        setRows(r.data || []);
        setDraft(Object.fromEntries((r.data || []).map((s) => [s.key, s.color])));
      })
      .catch(() => {});
  }, [isAdmin]);

  const dirty = rows.some((s) => (draft[s.key] || "").toLowerCase() !== (s.color || "").toLowerCase());

  const setColor = (key, val) => setDraft((d) => ({ ...d, [key]: val }));
  const resetOne = (s) => setColor(s.key, s.default);
  const resetAll = () => setDraft(Object.fromEntries(rows.map((s) => [s.key, s.default])));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/source-colors", { colors: draft });
      setRows(data || []);
      setDraft(Object.fromEntries((data || []).map((s) => [s.key, s.color])));
      toast.success("Cores atualizadas");
      onColorsChanged();
    } catch {
      toast.error("Não foi possível guardar as cores");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="sources-panel">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-[#18181b] border border-[#27272a] p-5 flex items-center gap-4">
          <TrendingUp className="w-6 h-6 text-[#10b981]" strokeWidth={1.5} />
          <div>
            <div className="label-tiny">Site com mais anúncios</div>
            <div className="font-head text-xl text-[#fafafa] mt-1">{most?.name || "—"}</div>
            <div className="text-xs text-[#71717a]">{most?.count || 0} perfis</div>
          </div>
        </div>
        <div className="bg-[#18181b] border border-[#27272a] p-5 flex items-center gap-4">
          <TrendingDown className="w-6 h-6 text-[#ef4444]" strokeWidth={1.5} />
          <div>
            <div className="label-tiny">Site com menos anúncios</div>
            <div className="font-head text-xl text-[#fafafa] mt-1">{least?.name || "—"}</div>
            <div className="text-xs text-[#71717a]">{least?.count || 0} perfis</div>
          </div>
        </div>
      </div>

      {/* Color editor (admin) */}
      {isAdmin && rows.length > 0 && (
        <div className="bg-[#18181b] border border-[#27272a]" data-testid="source-colors-editor">
          <div className="px-5 py-3 border-b border-[#27272a] flex items-center justify-between gap-3 flex-wrap">
            <span className="label-tiny inline-flex items-center gap-2"><Palette className="w-3.5 h-3.5" /> Cores por site</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                data-testid="colors-reset-all"
                onClick={resetAll}
                className="text-xs inline-flex items-center gap-1.5 border border-[#3f3f46] text-[#a1a1aa] hover:text-[#fafafa] hover:border-[#71717a] px-2.5 py-1.5 transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Repor padrão
              </button>
              <button
                type="button"
                data-testid="colors-save"
                onClick={save}
                disabled={!dirty || saving}
                className="text-xs inline-flex items-center gap-1.5 bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-3 py-1.5 font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Save className="w-3.5 h-3.5" />} Guardar cores
              </button>
            </div>
          </div>
          <p className="px-5 pt-3 text-xs text-[#71717a]">
            Escolha uma cor para cada site. A cor aparece nos cards de resultados e nos filtros, para identificar de relance a origem de cada anúncio.
          </p>
          <div className="p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((s) => {
              const val = draft[s.key] || s.color;
              const changed = (val || "").toLowerCase() !== (s.color || "").toLowerCase();
              return (
                <div key={s.key} data-testid={`color-row-${s.key}`} className="flex items-center gap-3 border border-[#27272a] px-3 py-2.5" style={{ borderLeft: `3px solid ${val}` }}>
                  <input
                    type="color"
                    data-testid={`color-input-${s.key}`}
                    value={val}
                    onChange={(e) => setColor(s.key, e.target.value)}
                    className="w-9 h-9 bg-transparent border border-[#3f3f46] cursor-pointer p-0.5 shrink-0"
                    title={`Cor de ${s.name}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-[#fafafa] truncate">{s.name}</div>
                    <input
                      type="text"
                      data-testid={`color-hex-${s.key}`}
                      value={val}
                      onChange={(e) => setColor(s.key, e.target.value)}
                      className="w-24 bg-[#09090b] border border-[#3f3f46] text-[#a1a1aa] font-mono text-[11px] px-2 py-0.5 outline-none focus:border-[#c8aa6e] mt-0.5"
                    />
                  </div>
                  {changed && (
                    <button
                      type="button"
                      onClick={() => resetOne(s)}
                      title="Repor cor padrão deste site"
                      className="text-[#71717a] hover:text-[#c8aa6e] shrink-0"
                      data-testid={`color-reset-${s.key}`}
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="bg-[#18181b] border border-[#27272a]">
        <div className="px-5 py-3 border-b border-[#27272a] label-tiny">Origens monitorizadas</div>
        <div className="divide-y divide-[#27272a]">
          {sources.map((s) => (
            <div key={s.key} data-testid={`source-${s.key}`} className="px-5 py-4 flex items-center gap-4">
              <span className="w-2 h-8" style={{ background: s.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[#fafafa]">{s.name}</div>
                <a href={s.base_url} target="_blank" rel="noreferrer" className="text-xs text-[#71717a] hover:text-[#c8aa6e] inline-flex items-center gap-1">
                  {s.base_url.replace(/^https?:\/\//, "").replace(/\/$/, "")} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="text-right">
                <div className="font-head text-lg text-[#c8aa6e]">{s.count}</div>
                <div className="text-[10px] text-[#71717a]">perfis</div>
              </div>
              <div className="w-48 text-right">
                <div className="text-xs text-[#a1a1aa] truncate">{s.last_status}</div>
                {s.last_new > 0 && <div className="text-[10px] text-[#10b981]">+{s.last_new} novos</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
